import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { MessageService } from 'primeng/api';
import { BehaviorSubject, EMPTY, filter, finalize, mergeMap, of, Subscription } from 'rxjs';

import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { MediaServiceErrorCodesEnum } from '../../../../api/mediaService/mediaResults.api.model';
import { MediaServiceAccessTypeEnum } from '../../../../api/mediaService/enums/media-service-access-type.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';

import { ControlValueAccessorModel, getProvider } from '../../../Forms/models/control-value-accessor.model';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { MediaServiceApi } from '../../../../api/mediaService/media-service.api';
import { ManageContentLibraryDialogComponent } from '../../../ContentLibrary/components/ManageContentLibraryDialog/manage-content-library-dialog.component';
import { ContentLibraryMediaService } from '../../../ContentLibrary/services/content-library-media.service';
import { ContentLibrarySearchService } from '../../../ContentLibrary/services/content-library-search.service';
import { UploadOption } from '../../../Forms/models/upload-option.model';
import { MediaSummaryModel } from '../../../ContentLibrary/models/content-library-media-summary.model';
import { ImageModel } from '../../../Misc/models/image.model';

@Component({
    selector: 'agpr-brand-logo',
    templateUrl: './brand-logo.component.html',
    styleUrls: ['./brand-logo.component.scss'],
    providers: [getProvider(BrandLogoComponent)],
    standalone: false
})
export class BrandLogoComponent extends ControlValueAccessorModel<ImageModel | ImageModel[]> implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;
    readonly acceptTypes = '.txt,.xls,.xlsx,.docx,.csv,.doc,.ppt,.pptx,.rtf,.png,.jpg,.jpeg,.gif,.odp,.ods,.odt,.pdf,.mp3,image/*';
    readonly urlFormat: RegExp = /^https?:\/\/[^\/]+\.[^\/]+(\/.*)?/;

    @Input() public id: string = 'brandLogo';
    @Input() public header: string;
    @Input() public headerTooltip: string;
    @Input() public showHeaderButton: boolean = true;
    @Input() public showSubHeader: boolean = false;
    @Input() public showSubHeaderButton: boolean = true;
    @Input() public useSplitButton: boolean = true;
    @Input() public uploadOptions: UploadOption[] = [
        {
            label: 'Mention.workbench.image.upload.Url',
            command: () => this.addImage(new ImageModel())
        },
        {
            label: 'Mention.workbench.image.upload.newImage',
            description: 'Mention.editDialog.image.browser.description',
            command: () => this.fileUploadInput.nativeElement.click()
        },
        {
            label:'Mention.workbench.image.upload.contentLibrary',
            command: () => this.openContentLibrary()
        }
    ];
    @Input() public uploadButtonLabel: string;
    @Input() public uploadButtonIcon: string;
    @Input() public maxFiles: number = 1;
    @Input() public disabled: boolean = false;
    @Input() public showThumbnail: boolean = true;

    @ViewChild('fileUploadInput') fileUploadInput: ElementRef<HTMLInputElement>;

    private overlayContainer: Element;
    private subscriptions: Subscription[] = [];

    mediaList: MediaSummaryModel[] = [];
    _value: ImageModel[] = [];
    imageErrors: boolean[] = [];

    constructor(
        private coreDialogService: CoreDialogService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        private mediaServiceApi: MediaServiceApi,
        private contentLibraryMediaService: ContentLibraryMediaService,
        private contentLibrarySearchService: ContentLibrarySearchService
    ) {
        super();
        this.overlayContainer = document.getElementById('main-content');
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.contentLibrarySearchService.mediaItems$.pipe(
                filter(x => !!x)
            ).subscribe(items => {
                this.mediaList = items;
                this.populateImages();
            })
        );
    }

    // For File Input
    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const files = input.files;

        if (!files?.length) {
            return;
        }

        const file = files[0];

        this.mediaServiceApi.upload([file], MediaServiceAccessTypeEnum.PUBLIC).pipe(
            mergeMap(response => {
                const mediaResult = response.mediaResults?.[0];

                if (mediaResult?.codedError) {
                    this.addUploadErrorMessage(file.name, mediaResult.codedError);
                    return EMPTY;
                }

                return of(mediaResult.media);
            }),
            finalize(() => {
                input.value = '';
            })
        ).subscribe({
            next: (media) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Mention.workbench.editMention.uploadNewImages.message')
                });

                const newImage = new ImageModel();
                newImage.source = media.url;
                newImage.name = media.name;
                this.addImage(newImage);

                this.contentLibraryMediaService.refreshAll();
                this.contentLibrarySearchService.refreshUsedStorage();
            },
            error: (error) => {
                this.addUploadErrorMessage(file.name, error);
            },
        });

    }

    openContentLibrary() {
        const maxFiles = this.maxFiles > 1 ? this.maxFiles - this._value.length : 1;

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        if (maxFiles < 1 ) {
            return;
        }

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                selectionMode: true,
                useAcceptedFilesTypes: true,
                multiSelect: true,
                maxFiles,
                navBarLaunch: false
            },
            outputs: {
                dialogClose: (selectedFiles: MediaSummaryModel[]) => {
                    if (!selectedFiles?.length) {
                        return;
                    }
                    selectedFiles.forEach(file => {
                        const newImage = new ImageModel();
                        newImage.source = file.url;
                        newImage.name = file.name;
                        this.addImage(newImage);
                    });
                }
            }
        };

        this.coreDialogService.open(ManageContentLibraryDialogComponent, io);
    }

    addUploadErrorMessage(resultName, error) {
        let summaryMessage;
        let detailMessage;
        switch (error.code) {
            case MediaServiceErrorCodesEnum.invalidType:
                summaryMessage = 'Common.mediaService.fileUpload.typeError.heading';
                detailMessage = 'Common.mediaService.fileUpload.typeError.message';
                break;
            case MediaServiceErrorCodesEnum.fileMaxSize:
                summaryMessage = 'Common.mediaService.fileUpload.maxSize.heading';
                detailMessage = 'Common.mediaService.fileUpload.maxSize.message';
                break;
            case MediaServiceErrorCodesEnum.insufficientSpace:
                summaryMessage = 'Common.mediaService.fileUpload.notEnoughSpace.heading';
                detailMessage = 'Common.mediaService.fileUpload.notEnoughSpace.message';
                break;
            case MediaServiceErrorCodesEnum.fileMinSize:
                summaryMessage = 'Common.mediaService.fileUpload.minSize.heading';
                detailMessage = 'Common.mediaService.fileUpload.minSize.message';
                break;
            case MediaServiceErrorCodesEnum.duplicateFile:
                summaryMessage = 'Common.lhp.libraryMoveDialog.upload.duplicate.label';
                detailMessage = 'Common.lhp.libraryMoveDialog.upload.duplicate.message';
                break;
            case MediaServiceErrorCodesEnum.internalError:
            default:
                summaryMessage = 'Common.mediaService.fileUpload.internalError.heading';
                detailMessage = 'Common.mediaService.fileUpload.internalError.message';
                break;
        }

        this.messageService.add({
            severity : messageSeverityEnum.error,
            summary: this.localizationService.transform(summaryMessage),
            detail: this.localizationService.transform(detailMessage, { name: resultName })
        });
    }

    addImage(image: ImageModel) {
        if (this.maxFiles > 1) {
            const index = this._value.findIndex(i => i.source === image.source);
            if (index >= 0) {
                this._value.splice(index, 1, image);
                this.imageErrors[index] = false;
            } else {
                this._value.push(image);
                this.imageErrors[this._value.length - 1] = false;
            }
        } else {
            this._value = [image];
            this.imageErrors[0] = false;
        }

        this.disabled = this.maxFiles > 1 && this._value.length >= this.maxFiles;

        // Emit if the url is not empty.
        if (image.source) {
            this.emitValue();
        }
    }

    removeImage(index: number) {
        this._value.splice(index, 1);
        this.imageErrors.splice(index, 1);
        this.emitValue();
    }

    emitValue() {
        this.value = this.maxFiles > 1 ? this._value : this._value[0] ?? null;
        this.onModelChange(this.value);
        this.onModelTouched();
    }

    override writeValue(value: ImageModel | ImageModel[]): void {
        if (!value) {
            this._value = [];
        } else if (Array.isArray(value)) {
            this._value = value;
        } else {
            this._value = [value];
        }
        this.imageErrors = [];
        this.populateImages();
    }

    override setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    private populateImages() {
        this._value.forEach(image => {
            const media = this.mediaList.find(x => x.url && x.url.trim() === image.source.trim());
            if (media && !image.name) {
                // pupulate image names
                image.name = media.name;
            }
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
